import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';
import { message } from 'antd';
import { HistoryOutlined, CommentOutlined, HighlightOutlined } from '@ant-design/icons';

import { ACCESS_TOKEN } from '../lib/cookie';
import { getLoginUser, changeId, changeName, changePassword, getMyRecentPosts, getMyComments, getMyAnnotations, getUserProfileImg } from '../api/user';

import { ProfileCard, ActivityWidget, NameChangeModal, IdChangeModal, PasswordChangeModal, ViewAllModal } from '../component/mypage';
import { UserInfo, RecentPost, MyComment, MyAnnotation, ActivityItem } from '../component/mypage/types';
import { useMessages } from '../i18n';

const ko = {
  title: '마이페이지',
  nameChanged: '이름이 성공적으로 변경되었습니다.',
  nameChangeFailed: '이름 변경에 실패했습니다.',
  idChanged: '아이디가 성공적으로 변경되었습니다. 다시 로그인해주세요.',
  idTaken: '이미 사용 중인 아이디입니다.',
  idChangeFailed: '아이디 변경에 실패했습니다.',
  passwordChanged: '비밀번호가 성공적으로 변경되었습니다.',
  passwordChangeFailed: '비밀번호 변경에 실패했습니다.',
  listLoadFailed: '목록을 불러오는데 실패했습니다.',
  pageLoadFailed: '마이페이지 정보를 불러오는데 실패했습니다.',
  viewAllPosts: '최근 본 게시물 전체보기',
  viewAllComments: '작성한 코멘트 전체보기',
  viewAllAnnotations: '작성한 어노테이션 전체보기',
  recentPosts: '최근 본 게시물',
  recentPostsEmpty: '아직 조회한 게시물이 없습니다.',
  recentPostsEmptySub: '이곳에 최근 본 게시물 목록이 표시됩니다.',
  comments: '작성한 코멘트',
  commentsEmpty: '작성한 코멘트가 없습니다.',
  commentsEmptySub: '이곳에 내가 작성한 슬라이드 코멘트가 표시됩니다.',
  annotations: '작성한 어노테이션',
  annotationsEmpty: '작성한 어노테이션이 없습니다.',
  annotationsEmptySub: '이곳에 내가 작성한 어노테이션이 표시됩니다.',
};

// 서버 오류 문구는 한국어라 영어 화면에서는 쓰지 않는다
const en: typeof ko = {
  title: 'My Page',
  nameChanged: 'Your name has been changed.',
  nameChangeFailed: 'Failed to change your name.',
  idChanged: 'Your username has been changed. Please log in again.',
  idTaken: 'This username is already taken.',
  idChangeFailed: 'Failed to change your username.',
  passwordChanged: 'Your password has been changed.',
  passwordChangeFailed: 'Failed to change your password.',
  listLoadFailed: 'Failed to load the list.',
  pageLoadFailed: 'Failed to load your page.',
  viewAllPosts: 'Recently Viewed Posts',
  viewAllComments: 'My Comments',
  viewAllAnnotations: 'My Annotations',
  recentPosts: 'Recently Viewed',
  recentPostsEmpty: "You haven't viewed any posts yet.",
  recentPostsEmptySub: 'Posts you have recently viewed will appear here.',
  comments: 'My Comments',
  commentsEmpty: "You haven't written any comments yet.",
  commentsEmptySub: 'Slide comments you have written will appear here.',
  annotations: 'My Annotations',
  annotationsEmpty: "You haven't created any annotations yet.",
  annotationsEmptySub: 'Annotations you have created will appear here.',
};

const MyPage = () => {
  const navigate = useNavigate();
  const t = useMessages({ ko, en });
  const isKo = t === ko;
  // 처음 한 번만 불러오는 effect 에서 현재 언어 문구를 읽기 위한 ref (언어가 바뀌어도 다시 불러오지 않는다)
  const tRef = useRef(t);
  tRef.current = t;
  // 서버가 준 오류 문구(한국어)는 한국어 화면에서만 쓴다
  const serverMessage = (error: any): string | undefined => (isKo ? error.response?.data?.message : undefined);
  
  const [userInfo, setUserInfo] = useState<UserInfo>({ name: '', username: '' });
  const [profileImageUrl, setProfileImageUrl] = useState<string>('');
  const [recentPosts, setRecentPosts] = useState<RecentPost[]>([]);
  const [comments, setComments] = useState<MyComment[]>([]);
  const [annotations, setAnnotations] = useState<MyAnnotation[]>([]);

  const [activeModal, setActiveModal] = useState<'name' | 'id' | 'password' | null>(null);
  const [loadingStates, setLoadingStates] = useState({ name: false, id: false, password: false });

  const [activeViewAll, setActiveViewAll] = useState<'posts' | 'comments' | 'annotations' | null>(null);
  const [fullListItems, setFullListItems] = useState<ActivityItem[]>([]);
  const [isFullListLoading, setIsFullListLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const PAGE_SIZE = 10;

  const closeModal = () => setActiveModal(null);
  const setLoading = (key: string, state: boolean) => setLoadingStates(prev => ({ ...prev, [key]: state }));

  const handleLogoutAndRedirect = (successMessage: string) => {
    sessionStorage.removeItem(ACCESS_TOKEN);
    message.success(successMessage);
    navigate('/login');
  };

  const onUpdateName = async (values: { name: string }) => {
    setLoading('name', true);
    try {
      await changeName(values.name);
      message.success(t.nameChanged);
      setUserInfo(prev => ({ ...prev, name: values.name }));
      closeModal();
    } catch (error: any) {
      message.error(serverMessage(error) || t.nameChangeFailed);
    } finally { setLoading('name', false); }
  };

  const onUpdateId = async (values: { newUsername: string; currentPassword: string }) => {
    setLoading('id', true);
    try {
      await changeId(values.newUsername, values.currentPassword);
      closeModal();
      handleLogoutAndRedirect(t.idChanged);
    } catch (error: any) {
      message.error(error.response?.status === 409 ? t.idTaken : serverMessage(error) || t.idChangeFailed);
    } finally { setLoading('id', false); }
  };

  const onChangePassword = async (values: { currentPassword: string; newPassword: string }) => {
    setLoading('password', true);
    try {
      await changePassword(values.currentPassword, values.newPassword);
      closeModal();
      message.success(t.passwordChanged);
    } catch (error: any) {
      message.error(serverMessage(error) || t.passwordChangeFailed);
    } finally { setLoading('password', false); }
  };

  const handleViewAll = async (type: 'posts' | 'comments' | 'annotations', page: number = 1) => {
    setActiveViewAll(type);
    setCurrentPage(page);
    setIsFullListLoading(true);

    try {
      let res;
      if (type === 'posts') {
        res = await getMyRecentPosts(page, PAGE_SIZE);
        setFullListItems(formatPosts(res?.data || []));
      } else if (type === 'comments') {
        res = await getMyComments(page, PAGE_SIZE);
        setFullListItems(formatComments(res?.data || []));
      } else if (type === 'annotations') {
        res = await getMyAnnotations(page, PAGE_SIZE);
        setFullListItems(formatAnnotations(res?.data || []));
      }
      
      const totalCount = res?.total || 0;
      setTotalItems(totalCount);
      
    } catch (error) {
      message.error(t.listLoadFailed);
    } finally {
      setIsFullListLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    if (activeViewAll) {
      handleViewAll(activeViewAll, page);
    }
  };

  const closeViewAllModal = () => {
    setActiveViewAll(null);
    setCurrentPage(1);
  };

  const getViewAllTitle = () => {
    if (activeViewAll === 'posts') return t.viewAllPosts;
    if (activeViewAll === 'comments') return t.viewAllComments;
    if (activeViewAll === 'annotations') return t.viewAllAnnotations;
    return '';
  };

  useEffect(() => {
    const fetchMyPageData = async () => {
      try {
        const userData = await getLoginUser();
        setUserInfo({ name: userData.name, username: userData.username });
        if (userData.profileImgPath) {
          const blobData = await getUserProfileImg(userData.profileImgPath);
          const url = URL.createObjectURL(blobData);
          setProfileImageUrl(url);
        }

        const [postsRes, commentsRes, annotationsRes] = await Promise.all([
          getMyRecentPosts(1, 5), getMyComments(1, 5), getMyAnnotations(1, 5)
        ]);

        setRecentPosts(postsRes?.data || postsRes || []);
        setComments(commentsRes?.data || commentsRes || []);
        setAnnotations(annotationsRes?.data || annotationsRes || []);
      } catch (error) {
        message.error(tRef.current.pageLoadFailed);
      }
    };
    fetchMyPageData();
  }, []);

  const formatPosts = (posts: RecentPost[]): ActivityItem[] => 
    posts.map(p => ({ id: p.postId, postId: p.postId, title: p.title, postTitle: p.title, date: p.viewedAt?.split('T')[0] || '' }));
  const formatComments = (comms: MyComment[]): ActivityItem[] => 
    comms.map(c => ({ id: c.commentId, postId: c.postId, title: c.content, postTitle: c.postTitle, date: c.createdAt?.split('T')[0] || '' }));
  const formatAnnotations = (annos: MyAnnotation[]): ActivityItem[] => 
    annos.map(a => ({ id: a.annotationId, postId: a.postId, title: a.content, postTitle: a.postTitle, date: a.createdAt?.split('T')[0] || '' }));

  const handleItemClick = (postId: number | string, postTitle: string) => {
    if (postId) {
      const params = new URLSearchParams();
      params.set('title', postTitle);
      params.set('from', 'mypage');
      navigate(`/post/${postId}?${params.toString()}`);
    }
  };

  return (
    <PageContainer>
      <DashboardWrapper>
        <HeaderArea><Title>{t.title}</Title></HeaderArea>

        <GridContainer>
          <ProfileSection>
            <ProfileCard userInfo={userInfo} onOpenModal={setActiveModal} profileImageUrl={profileImageUrl} onProfileImageChange={setProfileImageUrl} />
          </ProfileSection>

          <WidgetSection>
            <ActivityWidget 
              title={<><HistoryOutlined /> {t.recentPosts}</>}
              items={formatPosts(recentPosts)}
              emptyMessage={t.recentPostsEmpty}
              emptySubMessage={t.recentPostsEmptySub}
              onViewAll={() => handleViewAll('posts')}
              onItemClick={(item) => handleItemClick(item.postId, item.title)}
            />
            <ActivityWidget 
              title={<><CommentOutlined /> {t.comments}</>}
              items={formatComments(comments)}
              emptyMessage={t.commentsEmpty}
              emptySubMessage={t.commentsEmptySub}
              onViewAll={() => handleViewAll('comments')}
              onItemClick={(item) => handleItemClick(item.postId, item.postTitle)}
            />
            <ActivityWidget 
              title={<><HighlightOutlined /> {t.annotations}</>}
              items={formatAnnotations(annotations)}
              emptyMessage={t.annotationsEmpty}
              emptySubMessage={t.annotationsEmptySub}
              onViewAll={() => handleViewAll('annotations')}
              onItemClick={(item) => handleItemClick(item.postId, item.postTitle)}
            />
          </WidgetSection>
        </GridContainer>
      </DashboardWrapper>

      <NameChangeModal isOpen={activeModal === 'name'} onClose={closeModal} onSubmit={onUpdateName} isLoading={loadingStates.name} currentName={userInfo.name} />
      <IdChangeModal isOpen={activeModal === 'id'} onClose={closeModal} onSubmit={onUpdateId} isLoading={loadingStates.id} currentUsername={userInfo.username} />
      <PasswordChangeModal isOpen={activeModal === 'password'} onClose={closeModal} onSubmit={onChangePassword} isLoading={loadingStates.password} />

      <ViewAllModal 
        isOpen={activeViewAll !== null} 
        onClose={closeViewAllModal} 
        title={getViewAllTitle()} 
        items={fullListItems} 
        isLoading={isFullListLoading}
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={PAGE_SIZE}
        onPageChange={handlePageChange}
        onItemClick={(item: any) => handleItemClick(item.postId, item.postTitle)}
      />
    </PageContainer>
  );
};

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  min-height: 100vh;
  padding: 50px 20px;
  background-color: #f5f7fa;
  box-sizing: border-box;
  overflow-x: hidden;
`;

const DashboardWrapper = styled.div`
  width: 100%;
  max-width: 1400px;
  display: flex;
  flex-direction: column;
  gap: 30px;
  box-sizing: border-box;
`;

const HeaderArea = styled.div`
  margin-bottom: 10px;
`;

const Title = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #17375f;
  margin-bottom: 8px;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 350px minmax(0, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const ProfileSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const WidgetSection = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  min-width: 0;

  @media (max-width: 1024px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export default MyPage;